import React from 'react';
import { Link } from 'react-router-dom';
import { Table } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import {
  TImeEntryReportFilterOptions,
  TImeEntryReportFilters,
} from '@totem/components/ticketing/employeeUtilization/models';
import { TimeEntryReportItem } from '@totem/components/ticketing/types';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';
import {
  sortBooleanAscending,
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type Props = {
  data: TimeEntryReportItem[];
  updateFilters: (update: Partial<TImeEntryReportFilters>) => void;
  filters: TImeEntryReportFilters;
  filterOptions: TImeEntryReportFilterOptions;
};

const TimeEntryReportTable = ({
  data,
  filterOptions,
  updateFilters,
  filters,
}: Props) => {
  const getTotal = () => {
    return data.reduce(
      (acc, item) => acc + item.ticket.timeEntry.hoursLogged,
      0,
    );
  };

  const columns: ColumnProps<TimeEntryReportItem>[] = [
    {
      title: 'Date',
      dataIndex: 'ticket.timeEntry.date',
      key: 'ticket.timeEntry.date',
      render: (_, record: TimeEntryReportItem) =>
        `${formatDateOnly(record.ticket.timeEntry.date)}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.ticket.timeEntry.date,
          compB.ticket.timeEntry.date,
        ),
    },
    {
      title: 'Hours',
      dataIndex: 'ticket.timeEntry.hoursLogged',
      key: 'ticket.timeEntry.hoursLogged',
      render: (_, record: TimeEntryReportItem) => (
        <Hours amount={record.ticket.timeEntry.hoursLogged} />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(
          compA.ticket.timeEntry.hoursLogged,
          compB.ticket.timeEntry.hoursLogged,
        ),
    },
    {
      title: 'Billable',
      dataIndex: 'ticket.timeEntry.billable',
      key: 'billable',
      render: (_, record: TimeEntryReportItem) =>
        `${record.ticket.timeEntry.billable ? 'Yes' : 'No'}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortBooleanAscending(
          compA.ticket.timeEntry.billable,
          compB.ticket.timeEntry.billable,
        ),
      filterMultiple: false,
      filteredValue: filters.billable,
      filters: [
        {
          text: 'Yes',
          value: 'Yes',
        },
        {
          text: 'No',
          value: 'No',
        },
      ],
    },
    {
      title: 'Description',
      dataIndex: 'ticket.timeEntry.description',
      key: 'ticket.timeEntry.description',
      render: (_, record: TimeEntryReportItem) =>
        record.ticket.timeEntry.description,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.ticket.timeEntry.description,
          compB.ticket.timeEntry.description,
        ),
    },
    {
      title: 'Task',
      dataIndex: 'ticket.task.number',
      key: 'ticket.task.number',
      render: (_, record: TimeEntryReportItem) => {
        if (record.ticket.task !== null && record.ticket.task.name !== '') {
          return `${record.ticket.task.number}: ${record.ticket.task.name}`;
        }
        return '';
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.ticket.task.number, compB.ticket.task.number),
    },
    {
      title: 'Ticket Number',
      dataIndex: 'ticket.number',
      key: 'ticketNumber',
      render: (_, record: TimeEntryReportItem) => (
        <Link
          to={`/dashboard/ticket/${record.ticket.id}`}
          target={record.ticket.id}
        >
          {record.ticket.number}
        </Link>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.ticket.number, compB.ticket.number),
      filterMultiple: true,
      filteredValue: stringifyArray(filters.ticketNumber),
      filters:
        typeof filterOptions !== 'undefined' && filterOptions !== null
          ? filterOptions.ticketNumber.map((item) => ({
              text: item,
              value: item,
            }))
          : [],
    },
    {
      title: 'Summary',
      dataIndex: 'ticket.summary',
      key: 'summary',
      render: (_, record: TimeEntryReportItem) => record.ticket.summary,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.ticket.summary, compB.ticket.summary),
    },
    {
      title: 'Organization',
      dataIndex: 'organization.name',
      key: 'organization',
      render: (_, record: TimeEntryReportItem) => record.organization.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.organization.name, compB.organization.name),
      filterMultiple: true,
      filteredValue: stringifyArray(filters.organization),
      filters:
        typeof filterOptions !== 'undefined' && filterOptions !== null
          ? filterOptions.organization.map((item) => ({
              text: item,
              value: item,
            }))
          : [],
    },
  ];

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    newFilters: SorterResult<TimeEntryReportItem>,
  ) => {
    const { ...params } = newFilters;
    // @ts-ignore
    updateFilters({
      ...params,
    });
  };

  return (
    <Table
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(data) ? data : []}
      onChange={handleTableChange}
      pagination={{
        showSizeChanger: true,
        position: ['bottomRight'],
        showTotal: () => (
          <div
            style={{
              marginRight: 'auto',
              display: 'flex',
              justifyContent: 'left',
              paddingLeft: '8px',
            }}
          >
            <b>
              Total Hours <Hours amount={getTotal()} />
            </b>
          </div>
        ),
      }}
      rowKey={(record) => record.ticket.timeEntry.id}
    />
  );
};

export default TimeEntryReportTable;
